import { useCallback, useEffect, useRef, useState, type TransitionEvent } from "react";

import { runAfterEnterRafFrames } from "./helpers/insightsLoadingAnimation";
import {
    INSIGHTS_LOADING_TRANSITION_END_PROPERTY,
    INSIGHTS_LOADING_VISIBLE_MS,
} from "./insightsLoading.constants";
import { InsightsLoadingSlidePhase } from "./insightsLoading.types";
import { INSIGHTS_LOADING_ROTATION_KEYS } from "./texts";

const ROTATION_KEY_COUNT = INSIGHTS_LOADING_ROTATION_KEYS.length;

export const useInsightsLoadingRotatingSubtitle = () => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [slide, setSlide] = useState(InsightsLoadingSlidePhase.Enter);
    const slideRef = useRef(slide);
    const cancelEnterRef = useRef<(() => void) | null>(null);

    slideRef.current = slide;

    const beginEnterPhaseSequence = useCallback(() => {
        setSlide(InsightsLoadingSlidePhase.Enter);
        cancelEnterRef.current?.();
        cancelEnterRef.current = runAfterEnterRafFrames(() => {
            setSlide(InsightsLoadingSlidePhase.Idle);
            cancelEnterRef.current = null;
        });
    }, []);

    useEffect(() => {
        setActiveIndex(0);
        beginEnterPhaseSequence();

        return () => {
            cancelEnterRef.current?.();
            cancelEnterRef.current = null;
        };
    }, [beginEnterPhaseSequence]);

    useEffect(() => {
        if (slide !== InsightsLoadingSlidePhase.Idle) {
            return;
        }

        const id = window.setTimeout(() => {
            setSlide(InsightsLoadingSlidePhase.Exit);
        }, INSIGHTS_LOADING_VISIBLE_MS);

        return () => window.clearTimeout(id);
    }, [slide, activeIndex]);

    const onMotionTransitionEnd = useCallback(
        (event: TransitionEvent<HTMLElement>) => {
            if (
                event.propertyName !== INSIGHTS_LOADING_TRANSITION_END_PROPERTY ||
                slideRef.current !== InsightsLoadingSlidePhase.Exit
            ) {
                return;
            }

            setActiveIndex((i) => (i + 1) % ROTATION_KEY_COUNT);
            beginEnterPhaseSequence();
        },
        [beginEnterPhaseSequence],
    );

    return {
        messageKey: INSIGHTS_LOADING_ROTATION_KEYS[activeIndex],
        onMotionTransitionEnd,
        slide,
    };
};
